/**
 * Name-entry dialog shared by "New folder" and "Rename folder". Submitting
 * (Enter or the button) calls `onSubmit` with the trimmed name; the page owns
 * the mutation. The field resets to `initialName` each time the dialog opens.
 */

import { useState, useEffect, type FormEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export interface FolderNameDialogProps {
  open: boolean;
  mode: 'create' | 'rename';
  initialName?: string;
  /** When creating a subfolder, the parent's name — shown in the title for context. */
  parentName?: string;
  pending?: boolean;
  onSubmit: (name: string) => void;
  onOpenChange: (open: boolean) => void;
}

export function FolderNameDialog({
  open,
  mode,
  initialName = '',
  parentName,
  pending,
  onSubmit,
  onOpenChange,
}: FolderNameDialogProps) {
  const [name, setName] = useState(initialName);
  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const trimmed = name.trim();
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (trimmed) onSubmit(trimmed);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>
              {mode === 'rename'
                ? 'Rename folder'
                : parentName
                  ? `New subfolder in ${parentName}`
                  : 'New folder'}
            </DialogTitle>
          </DialogHeader>
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Folder name"
            maxLength={128}
            aria-label="Folder name"
            className="my-4"
          />
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!trimmed || pending}>
              {mode === 'create' ? 'Create' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
